import {
  ATV_BIKE_TYPE_ID,
  SNOWMOBILE_BIKE_TYPE_ID
} from "@/src/constants/vehicle";
import { VehicleType } from "@/src/types/types";

import atvBackPlaceholder from "@/assets/images/atv_back.webp";
import atvDashboardPlaceholder from "@/assets/images/atv_dashboard.webp";
import atvFrontPlaceholder from "@/assets/images/atv_front.webp";
import atvSidePlaceholder from "@/assets/images/atv_side.webp";
import atvTirePlaceholder from "@/assets/images/atv_wheel.webp";
import carBackPlaceholder from "@/assets/images/car_back.webp";
import carBackAnglePlaceholder from "@/assets/images/car_back_angle.webp";
import carDashboardPlaceholder from "@/assets/images/car_dashboard.webp";
import carFrontPlaceholder from "@/assets/images/car_front.webp";
import carFrontAnglePlaceholder from "@/assets/images/car_front_angle.webp";
import carFrontSeatPlaceholder from "@/assets/images/car_front_seat.webp";
import carInDoorPlaceholder from "@/assets/images/car_in_door.webp";
import carMaintenanceBookPlaceholder from "@/assets/images/car_maintenance_book.webp";
import carSidePlaceholder from "@/assets/images/car_side.webp";
import carTirePlaceholder from "@/assets/images/car_tire.webp";
import carTrunkPlaceholder from "@/assets/images/car_trunk.webp";
import carWindshieldPlaceholder from "@/assets/images/car_windshield.webp";
import caravanBackAnglePlaceholder from "@/assets/images/caravan_back_angle.webp";
import caravanBunkBedsPlaceholder from "@/assets/images/caravan_bunk_beds.webp";
import caravanFromKitchenPlaceholder from "@/assets/images/caravan_from_kitchen.webp";
import caravanFrontAnglePlaceholder from "@/assets/images/caravan_front_angle.webp";
import caravanInteriorFromEntrancePlaceholder from "@/assets/images/caravan_interior_from_entrance.webp";
import caravanSideLeftPlaceholder from "@/assets/images/caravan_side_left.webp";
import caravanSideRightPlaceholder from "@/assets/images/caravan_side_right.webp";
import caravanSofaGroupPlaceholder from "@/assets/images/caravan_sofa_group.webp";
import caravanToiletShowerPlaceholder from "@/assets/images/caravan_toilet_shower.webp";
import caravanTowardsCabinPlaceholder from "@/assets/images/caravan_towards_cabin.webp";
import moistMeasurementPlaceholder from "@/assets/images/moist_measurement.webp";
import motoBackPlaceholder from "@/assets/images/motorcycle_back.webp";
import chainAndSprocketPlaceholder from "@/assets/images/motorcycle_chain_sprockets.webp";
import dashboardPlaceholder from "@/assets/images/motorcycle_dashboard.webp";
import motoFrontPlaceholder from "@/assets/images/motorcycle_front.webp";
import motoSidePlaceholder from "@/assets/images/motorcycle_side.webp";
import motoTirePlaceholder from "@/assets/images/motorcycle_wheel.webp";
import snowmobileBackPlaceholder from "@/assets/images/snowmobile_back.webp";
import snowmobileFrontPlaceholder from "@/assets/images/snowmobile_front.webp";
import snowmobileSidePlaceholder from "@/assets/images/snowmobile_side.webp";
import snowmobileTrackPlaceholder from "@/assets/images/snowmobile_track.webp";

type DisplayVehicleType = "car" | "caravan" | "moto" | "atv" | "snowmobile";

interface MediaPerspectiveProps {
  bikeTypeId?: number;
  perspective: string;
  vehicleType: VehicleType;
}

type BaseVehicleImage = {
  sideRight: string;
  sideLeft: string;
  dashboard?: string;
  frontAngle?: string;
  backAngle?: string;
  frontSeat?: string;
  inDoor?: string;
  tire?: string;
  trunk?: string;
  maintenanceBook?: string;
  frontTire?: string;
  backTire?: string;
  rightTrack?: string;
  leftTrack?: string;
  damage?: string;
  chainAndSprocket?: string;
  interiorFromEntrance?: string;
  towardsCabin?: string;
  fromDriverArea?: string;
  sofaGroup?: string;
  fromKitchen?: string;
  toiletShower?: string;
  bunkBeds?: string;
  moistMeasurement?: string;
};

type VehicleImageMap = {
  [key in DisplayVehicleType]: BaseVehicleImage &
    (key extends "caravan"
      ? { front?: string; back?: string }
      : { front: string; back: string });
};

/**
 * Returns the appropriate vehicle image based on perspective and vehicle type
 * @param props MediaPerspectiveProps containing vehicle type, perspective and optional bike type ID
 * @returns URL string for the vehicle image
 */
export const mediaPerspective = ({
  bikeTypeId,
  perspective,
  vehicleType
}: MediaPerspectiveProps): string => {
  const vehicleImages: VehicleImageMap = {
    car: {
      frontAngle: carFrontAnglePlaceholder,
      front: carFrontPlaceholder,
      sideRight: carSidePlaceholder,
      sideLeft: carSidePlaceholder,
      backAngle: carBackAnglePlaceholder,
      back: carBackPlaceholder,
      dashboard: carDashboardPlaceholder,
      frontSeat: carFrontSeatPlaceholder,
      inDoor: carInDoorPlaceholder,
      tire: carTirePlaceholder,
      trunk: carTrunkPlaceholder,
      maintenanceBook: carMaintenanceBookPlaceholder,
      damage: carWindshieldPlaceholder
    },
    moto: {
      front: motoFrontPlaceholder,
      back: motoBackPlaceholder,
      sideRight: motoSidePlaceholder,
      sideLeft: motoSidePlaceholder,
      frontTire: motoTirePlaceholder,
      backTire: motoTirePlaceholder,
      dashboard: dashboardPlaceholder,
      damage: carWindshieldPlaceholder,
      maintenanceBook: carMaintenanceBookPlaceholder,
      chainAndSprocket: chainAndSprocketPlaceholder
    },
    atv: {
      front: atvFrontPlaceholder,
      back: atvBackPlaceholder,
      sideRight: atvSidePlaceholder,
      sideLeft: atvSidePlaceholder,
      frontTire: atvTirePlaceholder,
      backTire: atvTirePlaceholder,
      dashboard: atvDashboardPlaceholder,
      damage: carWindshieldPlaceholder
    },
    snowmobile: {
      front: snowmobileFrontPlaceholder,
      back: snowmobileBackPlaceholder,
      sideRight: snowmobileSidePlaceholder,
      sideLeft: snowmobileSidePlaceholder,
      rightTrack: snowmobileTrackPlaceholder,
      leftTrack: snowmobileTrackPlaceholder,
      dashboard: atvDashboardPlaceholder,
      damage: carWindshieldPlaceholder
    },
    caravan: {
      sideRight: caravanSideRightPlaceholder,
      sideLeft: caravanSideLeftPlaceholder,
      frontAngle: caravanFrontAnglePlaceholder,
      backAngle: caravanBackAnglePlaceholder,
      tire: carTirePlaceholder,
      maintenanceBook: carMaintenanceBookPlaceholder,
      damage: carWindshieldPlaceholder,
      interiorFromEntrance: caravanInteriorFromEntrancePlaceholder,
      towardsCabin: caravanTowardsCabinPlaceholder,
      sofaGroup: caravanSofaGroupPlaceholder,
      fromKitchen: caravanFromKitchenPlaceholder,
      toiletShower: caravanToiletShowerPlaceholder,
      bunkBeds: caravanBunkBedsPlaceholder,
      moistMeasurement: moistMeasurementPlaceholder
    }
  };

  /**
   * Determines the vehicle type based on input parameters
   * @returns "car", "caravan", "moto", "atv", or "snowmobile"
   */
  const getDisplayVehicleType = (
    vehicleType: VehicleType,
    bikeTypeId?: number
  ): DisplayVehicleType => {
    if (vehicleType === VehicleType.CARAVAN) {
      return "caravan";
    }
    if (vehicleType !== VehicleType.MOTO) {
      return "car";
    }
    if (bikeTypeId === ATV_BIKE_TYPE_ID) {
      return "atv";
    }
    if (bikeTypeId === SNOWMOBILE_BIKE_TYPE_ID) {
      return "snowmobile";
    }
    return "moto";
  };

  const selectedVehicle =
    vehicleImages[getDisplayVehicleType(vehicleType, bikeTypeId)];

  const CAR_ONLY_PERSPECTIVES = [
    "frontAngle",
    "backAngle",
    "frontSeat",
    "inDoor",
    "tire",
    "trunk"
  ];
  const TIRE_ONLY_PERSPECTIVES = ["frontTire", "backTire"];
  const SNOWMOBILE_ONLY_PERSPECTIVES = ["rightTrack", "leftTrack"];

  const isCarOnlyPerspective = CAR_ONLY_PERSPECTIVES.includes(perspective);
  const selectedType = getDisplayVehicleType(vehicleType, bikeTypeId);

  if (
    isCarOnlyPerspective &&
    selectedType !== "car" &&
    selectedType !== "caravan"
  ) {
    return "";
  }

  const isTirePerspective = TIRE_ONLY_PERSPECTIVES.includes(perspective);
  const isTrackPerspective = SNOWMOBILE_ONLY_PERSPECTIVES.includes(perspective);

  if (isTirePerspective && vehicleType !== VehicleType.MOTO) {
    return "";
  }

  if (isTrackPerspective && selectedType !== "snowmobile") {
    return "";
  }

  return selectedVehicle[perspective] || "";
};
